import { Audio } from 'expo-av'
import * as Sentry from '@sentry/react-native'
import {
  UI_SOUND_SOURCES,
  UiSoundId,
  getRegisteredUiSoundIds,
} from './ui-sound.registry'

const soundById = new Map<UiSoundId, Audio.Sound>()

let audioModeReady = false
let uiAudioEnabled = true
const UI_SOUND_VOLUME = 0.3

/**
 * Runtime-only audio toggle value.
 * Persistence is handled by FeatureProvider via AsyncStorage.
 */
export const setUiAudioEnabled = (enabled: boolean): void => {
  uiAudioEnabled = enabled
}

const ensureAudioMode = async (): Promise<void> => {
  if (audioModeReady) return
  try {
    await Audio.setAudioModeAsync({
      playsInSilentModeIOS: true,
      allowsRecordingIOS: false,
      staysActiveInBackground: false,
      shouldDuckAndroid: true,
      playThroughEarpieceAndroid: false,
    })
    audioModeReady = true
  } catch (error) {
    console.error('[ui-sound] setAudioModeAsync failed', error)
    Sentry.captureException(error)
  }
}

const loadSound = async (id: UiSoundId): Promise<Audio.Sound | null> => {
  const source = UI_SOUND_SOURCES[id]
  if (source == null) return null
  try {
    await ensureAudioMode()
    const { sound } = await Audio.Sound.createAsync(source, {
      shouldPlay: false,
      volume: UI_SOUND_VOLUME,
    })
    return sound
  } catch (error) {
    console.error('[ui-sound] load failed', id, error)
    Sentry.captureException(error)
    return null
  }
}

/**
 * Preload sounds to reduce first-tap latency. Call once after app mount (e.g. from App.tsx).
 */
export const preloadUiSounds = async (
  ids: UiSoundId[] = getRegisteredUiSoundIds()
): Promise<void> => {
  await Promise.all(
    ids.map(async (id) => {
      if (soundById.has(id)) return
      const sound = await loadSound(id)
      if (sound == null) return
      soundById.set(id, sound)
    })
  )
}

/**
 * Play a registered UI sound. Safe to call from press handlers (fire-and-forget: void playUiSound(...)).
 * Missing registry entries are no-ops.
 */
export const playUiSound = async (id: UiSoundId): Promise<void> => {
  if (!uiAudioEnabled) return
  if (UI_SOUND_SOURCES[id] == null) return
  try {
    await ensureAudioMode()
    let sound = soundById.get(id) ?? null
    if (sound === null) {
      sound = await loadSound(id)
      if (sound == null) return
      soundById.set(id, sound)
    }
    const status = await sound.getStatusAsync()
    if (!status.isLoaded) return
    await sound.setVolumeAsync(UI_SOUND_VOLUME)
    await sound.replayAsync()
  } catch (error) {
    console.error('[ui-sound] play failed', id, error)
    Sentry.captureException(error)
  }
}

export const unloadUiSounds = async (): Promise<void> => {
  const sounds = [...soundById.values()]
  soundById.clear()
  await Promise.all(
    sounds.map(async (sound) => {
      try {
        await sound.unloadAsync()
      } catch (error) {
        console.error('[ui-sound] unload failed', error)
        Sentry.captureException(error)
      }
    })
  )
  audioModeReady = false
}
